import React, { useEffect, useRef } from "react";
import { connect, ConnectedProps } from "react-redux";
import { actions, slotSelector } from "reducers/slotCreation";
import { actions as sessionActions, sessionSelector } from "reducers/session";
import { actions as notificationActions } from "reducers/notification";
import { AppDispatch, RootState } from "store";
import { SlotCreationModal } from "./SlotCreationModal";
import {
  SlotCreationModalContainerPropsType,
  SlotCreationValues,
} from "./types";
import { NotificationStateType } from "reducers/types/notification";
import { useTranslation } from "react-i18next";
import "./i18n";
import { dirtyAlert } from "utils/alerts";
import { FormRefType } from "utils/types";

const SlotCreationModalContainer: React.FC<
  SlotCreationModalContainerPropsType & ConnectorProps
> = (props): JSX.Element => {
  const formRef: FormRefType<SlotCreationValues> = useRef(null);
  const { t } = useTranslation();
  const { onClose, isOpen, regions } = props;
  const testId = "slot-creation";

  useEffect(() => {
    if (formRef.current && !isOpen) formRef.current.restart();
  }, [isOpen]);

  const onSubmit = (values: SlotCreationValues) => {
    props.showNotification({
      id: "notification-create-slot",
      level: "info",
      title: t("slotCreation:createSlotTitle"),
      body: t("slotCreation:createSlotMessage"),
      onConfirmLabel: "global:createConfirmation",
      onCancelLabel: "global:cancel",
      onConfirm: () => props.createSlot(values),
    });
  };
  const handleClose = () => {
    const close = () => {
      onClose();
      props.resetWorkzones();
      props.setDirty(false);
      props.setActualRegion("");
    };

    if (props.dirty) {
      props.showNotification(dirtyAlert({ onCancel: close, scroll: false }));
    } else {
      close();
    }
  };

  const onUpdateWorkzones = (region: string) => {
    if (region && region !== props.actualRegion) {
      props.getWorkzones(region);
      props.setActualRegion(region);
    }
  };
  return (
    <SlotCreationModal
      {...props}
      regions={regions}
      onUpdateWorkzones={onUpdateWorkzones}
      onSubmit={onSubmit}
      onClose={handleClose}
      formRef={formRef}
      testId={testId}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  dirty: sessionSelector(state).dirtyForm,
  workzones: slotSelector(state).workzones,
  actualRegion: slotSelector(state).actualRegion,
  fetchingWorkzones: slotSelector(state).fetchingWorkzones,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  getWorkzones: (regionId: string) => {
    dispatch(actions.getWorkzonesFetch(regionId));
  },
  resetWorkzones: () => {
    dispatch(actions.resetWorkzones());
  },
  setActualRegion: (regionId: string) => {
    dispatch(actions.setActualRegion(regionId));
  },
  createSlot: (values: SlotCreationValues) => {
    dispatch(actions.createSlotRequest(values));
  },
  setDirty: (dirty: boolean) => {
    dispatch(sessionActions.setDirty(dirty));
  },
  showNotification: (content: NotificationStateType) => {
    dispatch(notificationActions.showNotification(content));
  },
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type ConnectorProps = ConnectedProps<typeof connector>;

export default connector(SlotCreationModalContainer);
